import React, { useEffect, useState } from "react";
import ViewMoreBtn from "../../components/ViewMoreBtn";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { arrowRight } from "../../../../assets/svg/Svg";
import { NotePencil, Trash } from "@phosphor-icons/react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as cartSlice from "../../../../redux/slices/cartSlice";
import moment from "moment";
import DeleteModal from "../../components/DeleteModal";
import CartDetails from "./CartDetails";

const CartList = () => {
  const [data, setData] = useState([]);
  const [deletableId, setDeletableId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [viewModal, setViewModal] = useState(false);
  const [selectedCart, setSelectedCart] = useState(null);
  const [paymentFilter, setPaymentFilter] = useState("");
  const [tripStatusFilter, setTripStatusFilter] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  
  const { loading, cartList } = useSelector((state) => state.cartSlice);

  // Pagination states
  const paginationPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  // Fetch cart list on component mount
  useEffect(() => {
    dispatch(cartSlice.getCartList());
  }, [dispatch]);

  const handleViewUser = (data) => {
    setSelectedCart(data);
    setViewModal(true);
  };

  // Update data when cartList changes
  useEffect(() => {
    if (cartList && cartList.length > 0) {
      setData(cartList);
    } else {
      setData([]);
    }
  }, [cartList]);

  // Filter and paginate data
  useEffect(() => {
    // Filter data based on search text and payment filter
    let filtered = [...data];

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    if (searchText) {
      filtered = filtered.filter(
        (item) =>
          item.trip_name.toLowerCase().includes(searchText.toLowerCase()) ||
          item.id.toString() === searchText
      );
    }
    
    if (paymentFilter) {
      filtered = filtered.filter(
        (item) => item.payment_status.toString() === paymentFilter
      );
    }

    if (tripStatusFilter) {
      filtered = filtered.filter(
        (item) => item.status.toString() === tripStatusFilter
      );
    }
    
    setTotalRows(filtered.length);
    
    // Apply pagination to filtered data
    const paginatedData = filtered.slice(
      (currentPage - 1) * paginationPerPage,
      currentPage * paginationPerPage
    );
    
    setFilteredData(paginatedData);
  }, [data, searchText, paymentFilter, tripStatusFilter, currentPage, paginationPerPage]);

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.id,
      sortable: true,
    },
    {
      name: "Created Date",
      selector: (row) => moment(new Date(row?.created_at)).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: "Trip Name",
      selector: (row) => row?.trip_name,
      sortable: true,
    },
    {
      name: "Payment Status",
      selector: (row) => (row?.payment_status === 1 ? "Success" : "Pending"),
      sortable: true,
    },
    {
      name: "Trip Status",
      selector: (row) => {
        switch (row?.status) {
          case 0:
            return "Pending";
          case 1:
            return "Completed";
          case 2:
            return "Cancelled";
          case 3:
            return "Upcoming";
          default:
            return "Unknown";
        }
      },
      sortable: true,
    },
    {
      name: "Start Date",
      selector: (row) => moment(new Date(row?.start_date)).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: "End Date",
      selector: (row) => moment(new Date(row?.end_date)).format("DD-MM-YYYY"),
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="d-flex table_action">
          <div
            className="view_more"
            onClick={() => navigate(`/cart/${row?.id}`)}
          >
            <ViewMoreBtn />
          </div>
        </div>
      ),
    },
  ];

  const handleDeleteUser = () => {
    // Implement delete logic here
    // dispatch(cartSlice.deleteCartItem(deletableId, setDeletableId, setDeleteModal));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1); // Reset to first page when searching
    setResetPaginationToggle(!resetPaginationToggle);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const paginationOptions = {
    rowsPerPageText: "Rows per page:",
    rangeSeparatorText: "of",
    noRowsPerPage: false,
    selectAllRowsItem: false,
    selectAllRowsItemText: "All",
  };

  const customPagination = () => {
    const page = Math.ceil(totalRows / paginationPerPage);
    const pageButtons = [];
    const startPage = Math.max(currentPage - 1, 1);
    const endPage = Math.min(currentPage + 1, page);

    // Always add the first page
    pageButtons.push(
      <button
        key={1}
        className={`btn-pagination ${currentPage === 1 ? "active" : ""}`}
        onClick={() => handlePageChange(1)}
      >
        1
      </button>
    );

    // Ellipsis after the first page if necessary
    if (startPage > 2) {
      pageButtons.push(
        <span key="ellipsis-start" className="pagination-ellipsis">
          &hellip;
        </span>
      );
    }

    // Range of buttons around the current page
    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== page) {
        pageButtons.push(
          <button
            key={i}
            className={`btn-pagination ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }
    }

    // Ellipsis before the last page if necessary
    if (endPage < page - 1) {
      pageButtons.push(
        <span key="ellipsis-end" className="pagination-ellipsis">
          &hellip;
        </span>
      );
    }

    // Always add the last page
    if (page !== 1) {
      pageButtons.push(
        <button
          key={page}
          className={`btn-pagination ${currentPage === page ? "active" : ""}`}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      );
    }

    return (
      <div className="pagination-container mt-5">
        <button
          className="btn-pagination btn-pagi_prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {arrowRight}
        </button>
        <span>{pageButtons}</span>
        <button
          className="btn-pagination btn-pagi_next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === page}
        >
          {arrowRight}
        </button>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <div className="w-100 d-flex">
          <div
            className="spinner-border text-primary mx-auto"
            role="status"
          ></div>
        </div>
      ) : (
        <>
          <div>
            <div className="page-header">
              <h3 className="page-title">Cart</h3>
            </div>
            <div className="row">
              <div className="col-lg-12 grid-margin stretch-card">
                <div className="card">
                  <div className="card-body">
                    <DataTable
                      title={<h6 className="card-title">Cart List</h6>}
                      columns={columns}
                      data={filteredData}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      onChangePage={handlePageChange}
                      paginationComponentOptions={paginationOptions}
                      paginationComponent={customPagination}
                      highlightOnHover
                      pointerOnHover
                      subHeader
                      subHeaderAlign="left"
                      subHeaderComponent={
                        <Row className="mb-3">
                          <Col md={6}>
                            <label htmlFor="search-trip-name">
                              Search by Trip Name
                            </label>
                            <input
                              type="text"
                              id="search-trip-name"
                              className="form-control"
                              placeholder="Search by trip name"
                              value={searchText}
                              onChange={(e) => handleSearch(e.target.value)}
                            />
                          </Col>
                          <Col md={6}>
                            <label htmlFor="payment-status">
                              Payment Status
                            </label>
                            <select
                              id="payment-status"
                              className="form-control"
                              value={paymentFilter}
                              onChange={(e) => {
                                setPaymentFilter(e.target.value);
                                setCurrentPage(1); // Reset to first page when filter changes
                              }}
                            >
                              <option value="">Select Payment Status</option>
                              <option value="1">Success</option>
                              <option value="0">Pending</option>
                            </select>
                          </Col>
                          <Col md={6}>
                            <label htmlFor="trip-status">Trip Status</label>
                            <select
                              id="trip-status"
                              className="form-control"
                              value={tripStatusFilter}
                              onChange={(e) => {
                                setTripStatusFilter(e.target.value);
                                setCurrentPage(1); // Reset to first page when filter changes
                              }}
                            >
                              <option value="">Select Trip Status</option>
                              <option value="0">Pending</option>
                              <option value="1">Completed</option>
                              <option value="2">Cancelled</option>
                              <option value="3">Upcoming</option>
                            </select>
                          </Col>
                        </Row>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DeleteModal
            deleteModal={deleteModal}
            setDeleteModal={setDeleteModal}
            handleDelete={handleDeleteUser}
            name={"Cart Item"}
          />
        </>
      )}
    </>
  );
};

export default CartList;